import { useEffect } from 'react';
import api from '../services/api';

const useGoogleAnalytics = () => {
    useEffect(() => {
        const loadGoogleAnalytics = async () => {
            try {
                // Fetch public site settings including GA scripts
                const response = await api.get('/site-settings');
                
                if (response.data.success) {
                    const data = response.data.data;
                    const headScript = data.google_analytics_head_script;
                    const bodyScript = data.google_analytics_body_script;
                    
                    // Check if GA script is already loaded to avoid duplicates
                    const existingGAScripts = document.querySelectorAll('script[src*="googletagmanager.com/gtag/js"]');
                    
                    if (headScript && existingGAScripts.length === 0) {
                        console.log('Loading Google Analytics...');
                        
                        // Inject head script
                        const headContainer = document.createElement('div');
                        headContainer.innerHTML = headScript;
                        
                        // Extract and inject head scripts
                        const headScripts = headContainer.querySelectorAll('script');
                        headScripts.forEach(scriptElement => {
                            const newScript = document.createElement('script');
                            if (scriptElement.src) {
                                newScript.src = scriptElement.src;
                                newScript.async = true;
                                
                                // Parse measurement ID for tracking
                                const measurementIdMatch = scriptElement.src.match(/id=([^&]+)/);
                                if (measurementIdMatch) {
                                    window.GA_TRACKING_ID = measurementIdMatch[1];
                                }
                            } else {
                                newScript.innerHTML = scriptElement.innerHTML;
                                
                                // Parse measurement ID from config
                                const configMatch = scriptElement.innerHTML.match(/gtag\('config',\s*'([^']+)'/);
                                if (configMatch) {
                                    window.GA_TRACKING_ID = configMatch[1];
                                }
                            }
                            document.head.appendChild(newScript);
                        });
                        
                        console.log('Google Analytics head script loaded successfully');
                    }
                    
                    // Inject body script if provided
                    if (bodyScript) {
                        // Check if body script is already injected
                        const existingBodyScript = document.querySelector('noscript iframe[src*="googletagmanager.com"]');
                        
                        if (!existingBodyScript) {
                            // Wait for document body to be available
                            const injectBodyScript = () => {
                                if (document.body) {
                                    const bodyContainer = document.createElement('div');
                                    bodyContainer.innerHTML = bodyScript;
                                    
                                    // Extract and inject body elements (like noscript)
                                    const bodyElements = bodyContainer.children;
                                    Array.from(bodyElements).forEach(element => {
                                        document.body.insertBefore(element.cloneNode(true), document.body.firstChild);
                                    });
                                    
                                    console.log('Google Analytics body script loaded successfully');
                                } else {
                                    // Body not ready yet, try again
                                    setTimeout(injectBodyScript, 50);
                                }
                            };
                            
                            injectBodyScript();
                        }
                    }
                    
                    if (!headScript && !bodyScript) {
                        console.log('No Google Analytics scripts to load');
                    } else if (existingGAScripts.length > 0) {
                        console.log('Google Analytics already loaded');
                    }
                }
            } catch (error) {
                console.error('Failed to load Google Analytics:', error);
            }
        };

        // Load GA when the component mounts
        loadGoogleAnalytics();
    }, []);

    // Return tracking functions that can be used by components
    const trackEvent = (action, category = 'engagement', label = null, value = null) => {
        if (typeof window !== 'undefined' && window.gtag) {
            const eventData = {
                event_category: category,
                event_label: label,
                value: value
            };
            
            // Remove null values
            Object.keys(eventData).forEach(key => {
                if (eventData[key] === null) delete eventData[key];
            });
            
            window.gtag('event', action, eventData);
        }
    };

    const trackPageView = (page_title, page_location) => {
        if (typeof window !== 'undefined' && window.gtag) {
            window.gtag('config', window.GA_TRACKING_ID, {
                page_title: page_title,
                page_location: page_location,
            });
        }
    };

    return {
        trackEvent,
        trackPageView
    };
};

export default useGoogleAnalytics;